import React, { createContext, useState, useContext, useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";
import { useSearchParams } from "react-router-dom";
import { state, view } from "../variables"; // Import state and view from variables.js

// Create the context
const PFDataContext = createContext();

// Provider Component
const PFDataProvider = ({ children }) => {

  const [searchParams] = useSearchParams();

  const initialFilters = {
    brand: [],
    category: [],
    search: "",
    strain: [],
    thcRange: [],
    priceRange: [],
    isSpecialPromo: false,
  };



  // // Get initial state from the WordPress shortcode div
  // const getInitialState = () => {
  //   if (typeof document !== "undefined") {
  //     const pfElement = document.getElementById("pf__products");
  //     if (pfElement) {
  //       return pfElement.getAttribute("data-state") || "CA"; // Default to "CA"
  //     }
  //   }
  //   return "CA"; // Fallback if document is not available
  // };

  // // get initial view from the WordPress shortcode div
  // const getInitialView = () => {
  //   if (typeof document !== "undefined") {
  //     const pfElement = document.getElementById("pf__products");
  //     if (pfElement) {
  //       return pfElement.getAttribute("data-view") || "public"; // Default to "public"
  //     }
  //   }
  //   return "public"; // Fallback if document is not available
  // };



  // Initial State
  const initialPFData = {
    pfProducts: {}, // Stores all products
    filteredpfProducts: {}, // Stores filtered products
    pfProductBrands: [], // Stores brand names for filters
    pfProductCategories: [], // Stores category names for filters
    pfProductStrains: [], // Stores strain names for filters
    pfButton1: { text: "", url: "", show: true },
    pfButton2: { text: "", url: "", show: true },
    pfButton3: { text: "", url: "", show: true },
    // pfState: getInitialState(), // Default to "CA"
    // pfView: getInitialView(), // Default to "public"
    isLoading: true,
    pfState: state, 
    pfView: view,
    filter: initialFilters,
    cart : {
      products: []
    },
    storeEmails: {}
  };

  // State Management
  const [pfData, setPfData] = useState(initialPFData);
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Function to apply filters from the URL
  const applyFiltersFromURL = () => {
    console.log("Applying Filters from URL");
    const filtersFromURL = {};
  
    Object.keys(initialFilters).forEach((key) => {
      const param = searchParams.get(key);
      if (param) {
        try {
          filtersFromURL[key] = JSON.parse(param);
        } catch (e) {
          filtersFromURL[key] = param;
        }
      }
    });
  
    setPfData((prev) => {
      const updatedFilters = { ...prev.filter, ...filtersFromURL };
      
      return {
        ...prev,
        filter: updatedFilters,
        filteredpfProducts: applyFilters(updatedFilters, prev.pfProducts) // Immediately apply filters
      };
    });
  };
  

  useEffect(() => {
    applyFiltersFromURL();
  }, [searchParams]);

  
  const applyFilters = () => {
    let products = cloneDeep(pfData.pfProducts);
    let filters = pfData.filter;
    let filteredProducts = cloneDeep(products);

    // console.log("Applying Filters:", filters);
    // console.log("Original Products:", products);

    // Filter by brand
    if (filters.brand && Array.isArray(filters.brand) && filters.brand.length > 0) {
      const brands = filters.brand.map((b) => b.toLowerCase());
      filteredProducts = Object.fromEntries(
        Object.entries(filteredProducts).filter(([brand]) =>
          brands.includes(brand.toLowerCase())
        )
      );
    }

    // Filter by category
    if (filters.category && Array.isArray(filters.category)) {
      const categories = filters.category.map((c) => c.toLowerCase());

      if (categories.length > 0) {
        Object.keys(filteredProducts).forEach((brand) => {
          if (
            filteredProducts[brand].categories &&
            typeof filteredProducts[brand].categories === "object"
          ) {
            const filteredCategories = Object.fromEntries(
              Object.entries(filteredProducts[brand].categories).filter(([category]) =>
                categories.includes(category.toLowerCase())
              )
            );

            if (Object.keys(filteredCategories).length > 0) {
              filteredProducts[brand].categories = filteredCategories;
            } else {
              delete filteredProducts[brand];
            }
          }
        });
      }
    }

    // Filter by strain
    if (filters.strain && Array.isArray(filters.strain) && filters.strain.length > 0) {
      const strains = filters.strain.map((s) => s.toLowerCase());

      Object.keys(filteredProducts).forEach((brand) => {
        if (
          filteredProducts[brand].categories &&
          typeof filteredProducts[brand].categories === "object"
        ) {
          Object.keys(filteredProducts[brand].categories).forEach((category) => {
            const filteredProductsArray = filteredProducts[brand].categories[category].filter(
              (product) => product.strain && strains.includes(product.strain.toLowerCase())
            );

            if (filteredProductsArray.length > 0) {
              filteredProducts[brand].categories[category] = filteredProductsArray;
            } else {
              delete filteredProducts[brand].categories[category];
            }
          });

          if (Object.keys(filteredProducts[brand].categories).length === 0) {
            delete filteredProducts[brand];
          }
        }
      });
    }

    // Filter by THC percentage
    const thcMin = parseFloat(filters.thcRange[0]) || 0;
    const thcMax = parseFloat(filters.thcRange[1]) || 100;

    Object.keys(filteredProducts).forEach((brand) => {
      if (
        filteredProducts[brand].categories &&
        typeof filteredProducts[brand].categories === "object"
      ) {
        Object.keys(filteredProducts[brand].categories).forEach((category) => {
          const filteredProductsArray = filteredProducts[brand].categories[category].filter(
            (product) =>
              product.thc_percentage >= thcMin &&
              product.thc_percentage <= thcMax
          );

          if (filteredProductsArray.length > 0) {
            filteredProducts[brand].categories[category] = filteredProductsArray;
          } else {
            delete filteredProducts[brand].categories[category];
          }
        });

        if (Object.keys(filteredProducts[brand].categories).length === 0) {
          delete filteredProducts[brand];
        }
      }
    });

    // Filter by price
    const priceMin = parseFloat(filters.priceRange[0]) || 0;
    const priceMax = parseFloat(filters.priceRange[1]) || Infinity;

    Object.keys(filteredProducts).forEach((brand) => {
      if (
        filteredProducts[brand].categories &&
        typeof filteredProducts[brand].categories === "object"
      ) {
        Object.keys(filteredProducts[brand].categories).forEach((category) => {
          const filteredProductsArray = filteredProducts[brand].categories[category].filter(
            (product) =>
              product.price_per_unit >= priceMin &&
              product.price_per_unit <= priceMax
          );

          if (filteredProductsArray.length > 0) {
            filteredProducts[brand].categories[category] = filteredProductsArray;
          } else {
            delete filteredProducts[brand].categories[category];
          }
        });

        if (Object.keys(filteredProducts[brand].categories).length === 0) {
          delete filteredProducts[brand];
        }
      }
    });


     // Filter by search keyword
     if (filters.search && filters.search.trim() !== "") {
      const searchKeyword = filters.search.trim().toLowerCase();
      Object.keys(filteredProducts).forEach((brand) => {
          if (filteredProducts[brand].categories) {
              Object.keys(filteredProducts[brand].categories).forEach((category) => {
                  const filteredProductsArray = filteredProducts[brand].categories[category].filter(
                      (product) =>
                          brand.toLowerCase().includes(searchKeyword) ||
                          category.toLowerCase().includes(searchKeyword) ||
                          (product.sku_name && product.sku_name.toLowerCase().includes(searchKeyword))
                  );

                  if (filteredProductsArray.length > 0) {
                      filteredProducts[brand].categories[category] = filteredProductsArray;
                  } else {
                      delete filteredProducts[brand].categories[category];
                  }
              });

              if (Object.keys(filteredProducts[brand].categories).length === 0) {
                  delete filteredProducts[brand];
              }
          }
      });
      }


     // Filter products by isSpecialPromo
    if (filters.isSpecialPromo) {
      Object.keys(filteredProducts).forEach((brand) => {
        if (
          filteredProducts[brand].categories &&
          typeof filteredProducts[brand].categories === "object"
        ) {
          Object.keys(filteredProducts[brand].categories).forEach((category) => {
            const filteredProductsArray = filteredProducts[brand].categories[category].filter(
              (product) => parseInt(product.isSpecialPromo) === 1
            );

            if (filteredProductsArray.length > 0) {
              filteredProducts[brand].categories[category] = filteredProductsArray;
            } else {
              delete filteredProducts[brand].categories[category];
            }
          });

          if (Object.keys(filteredProducts[brand].categories).length === 0) {
            delete filteredProducts[brand];
          }
        }
      });
    }



    // console.log("Final Filtered Products:", filteredProducts);
    return filteredProducts;
};

  
  
 const updatePfData = (propertyOrUpdates, value) => {
  
    setPfData((prevPfData) => {

      if(propertyOrUpdates in prevPfData.filter) {
        // console.log("Updating filter:", propertyOrUpdates, value);
        // Update individual keys inside the filter object
        return {
          ...prevPfData,
          filter: {
            ...prevPfData.filter,
            [propertyOrUpdates]: value,
          },
        };

      }else if(propertyOrUpdates in prevPfData.cart) {
        // console.log("Updating cart:", propertyOrUpdates, value);
        // Update individual keys inside the cart object
        return {
          ...prevPfData,
          cart: {
            ...prevPfData.cart,
            [propertyOrUpdates]: value,
          },
        };
      }else{
        if(typeof propertyOrUpdates === "object") {
          // console.log("Updating multiple properties:", propertyOrUpdates);
          return {
            ...prevPfData,
            ...propertyOrUpdates,
          };
        }
  
        // otherwise, update the property with the new value
        if(propertyOrUpdates in prevPfData) {
          // console.log("Updating property:", propertyOrUpdates, value);
          return {
            ...prevPfData,
            [propertyOrUpdates]: value,
          };
        }
      }
      
      
      return prevPfData;
    });
 };

  // Alert function to set a message and clear it after a timeout
  const DEFAULT_ALERT_DURATION = 3000;
  const showAlert = (message, duration = DEFAULT_ALERT_DURATION) => {
    setAlertMessage(message);
    setTimeout(() => setAlertMessage(""), duration);
  };

  // Log pfData whenever it changes
  useEffect(() => {
    console.log("PFData updated:", pfData);
  }, [pfData]);

  // useEffect - when the filter changes, update the filtered products
  // useEffect(() => {
  //   // Apply filters to update `filteredpfProducts`
  //   setPfData((prevPfData) => ({
  //     ...prevPfData,
  //     filteredpfProducts: applyFilters(),
  //   }));
  // }, [pfData.filter ]);

  useEffect(() => {
    if (Object.keys(pfData.pfProducts).length > 0) {
      setPfData((prevPfData) => ({
        ...prevPfData,
        filteredpfProducts: applyFilters(prevPfData.filter, prevPfData.pfProducts),
      }));
    }
  }, [pfData.filter, pfData.pfProducts]); // Ensure it runs when `pfProducts` is updated
  

  // useEffect to ensure `pfState` is updated dynamically on mount
  // useEffect(() => {
  //   const pfElement = document.getElementById("pf__products");
  //   if (pfElement) {
  //     const stateFromDiv = pfElement.getAttribute("data-state");
  //     if (stateFromDiv) {
  //       setPfData((prev) => ({
  //         ...prev,
  //         pfState: stateFromDiv,
  //       }));
  //     }
  //   }
  // }, []);

  // useEffect to ensure `pfView` is updated dynamically on mount
  // useEffect(() => {
  //   const pfElement = document.getElementById("pf__products");
  //   if (pfElement) {
  //     const viewFromDiv = pfElement.getAttribute("data-view");
  //     if (viewFromDiv) {
  //       setPfData((prev) => ({
  //         ...prev,
  //         pfView: viewFromDiv,
  //       }));
  //     }
  //   }
  // }, []);

  return (
    <PFDataContext.Provider
      value={{ pfData, setPfData, updatePfData, showAlert, alertMessage }}
    >
      {children}
    </PFDataContext.Provider>
  );
};

// Custom Hook for Consuming the Context
const usePFDataContext = () => {
  const context = useContext(PFDataContext);
  if (!context) {
    throw new Error("usePFDataContext must be used within a PFDataProvider");
  }
  return context;
};

export { PFDataProvider, usePFDataContext };
